import React, { useCallback, useEffect, useState } from "react";
import {
  qformat,
  DATE_FORMATS,
  formatRelativeVerbose,
  daysSince,
} from "views_shared/app/javascript/utils/dateFormatters";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import DiagnosticSummary from "../notification_diagnostics/DiagnosticSummary";
import DiagnosticModal from "../notification_diagnostics/DiagnosticModal";
import SendgridSuppressionCard from "../sendgrid_suppressions/SendgridSuppressionCard";
import LoadingState from "../shared/LoadingState";
import ErrorState from "../shared/ErrorState";

// Utility functions

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = [];

  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, "...", total);
  } else if (current >= total - 3) {
    pages.push(1, "...", total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, "...", current - 1, current, current + 1, "...", total);
  }

  return pages;
}

// Card view (for overview page)

function NotificationCardView({ diagnostics, userSlug }) {
  if (!diagnostics) {
    return (
      <div className="card mb-4 border-secondary" style={{ borderLeftWidth: "4px" }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="card-title text-muted">
                <i className="fa fa-bell-slash me-2" />
                Notification Status Unknown
              </h5>
              <p className="card-text text-muted mb-0">No notification diagnostics available for this user.</p>
            </div>
            <a href={`/admin/users/${userSlug}/notifications`} className="btn btn-outline-primary btn-sm">
              Details <i className="fa fa-arrow-right ms-1" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const hasIssues =
    !diagnostics.in_good_standing ||
    diagnostics.emails_7d === 0 ||
    (diagnostics.last_email_at && daysSince(diagnostics.last_email_at) > 7);

  const borderClass = hasIssues ? "border-warning" : "border-success";
  const iconClass = hasIssues ? "text-warning" : "text-success";
  const statusIcon = hasIssues ? "fa-exclamation-triangle" : "fa-check-circle";

  return (
    <div className={`card mb-4 ${borderClass}`} style={{ borderLeftWidth: "4px" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="card-title">
              <i className={`fa ${statusIcon} ${iconClass} me-2`} />
              {hasIssues ? "Notification Issues Detected" : "Notifications Healthy"}
            </h5>
            <div className="row mt-3">
              <div className="col-auto">
                <small className="text-muted d-block">Emails (7 days)</small>
                <span className="fs-5 fw-semibold">{diagnostics.emails_7d || 0}</span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Emails (30 days)</small>
                <span className="fs-5 fw-semibold">{diagnostics.emails_30d || 0}</span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Last Email</small>
                <span className="fs-5 fw-semibold">{formatRelativeVerbose(diagnostics.last_email_at)}</span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Standing</small>
                <span className={`fs-5 fw-semibold ${diagnostics.in_good_standing ? "text-success" : "text-danger"}`}>
                  {diagnostics.in_good_standing ? "Good" : "Issues"}
                </span>
              </div>
            </div>
          </div>
          <a href={`/admin/users/${userSlug}/notifications`} className="btn btn-outline-primary btn-sm">
            Details <i className="fa fa-arrow-right ms-1" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Full view components

function StatusBadge({ status, type }) {
  if (!status) return <span className="text-muted">—</span>;

  const statusClasses = {
    delivery: {
      delivered: "bg-success",
      sent: "bg-info",
      pending: "bg-warning",
      failed: "bg-danger",
      bounced: "bg-danger",
    },
    engagement: {
      opened: "bg-success",
      clicked: "bg-primary",
      none: "bg-secondary",
    },
  };

  const badgeClass = statusClasses[type]?.[status.toLowerCase()] || "bg-secondary";

  return <span className={`badge ${badgeClass}`}>{status}</span>;
}

function SourceBadge({ source }) {
  const badgeClass = source === "v2" ? "bg-primary" : "bg-secondary";
  return <span className={`badge ${badgeClass}`}>{source.toUpperCase()}</span>;
}

function Pagination({ meta, onPageChange }) {
  const { page, total_pages: totalPages, total_count: totalCount, per_page: perPage } = meta;

  if (totalPages <= 1) {
    return (
      <div className="text-muted small mt-2">
        Showing {totalCount} notification{totalCount !== 1 ? "s" : ""}
      </div>
    );
  }

  const startItem = (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, totalCount);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="text-muted small">
        Showing {startItem}–{endItem} of {totalCount}
      </div>
      <nav aria-label="Notification pagination">
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
            <button type="button" className="page-link" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
              Previous
            </button>
          </li>
          {generatePageNumbers(page, totalPages).map((p) => {
            const key = typeof p === "number" ? `page-${p}` : `ellipsis-${Math.random()}`;
            return (
              <li key={key} className={`page-item ${p === page ? "active" : ""} ${p === "..." ? "disabled" : ""}`}>
                {p === "..." ? (
                  <span className="page-link">...</span>
                ) : (
                  <button type="button" className="page-link" onClick={() => onPageChange(p)}>
                    {p}
                  </button>
                )}
              </li>
            );
          })}
          <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}

function NotificationRow({ notification, columns }) {
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>;

    switch (key) {
      case "created_at":
      case "seen_at":
      case "clicked_at":
      case "opened_at":
        return qformat(value, DATE_FORMATS.dateTime);
      case "delivery_status":
        return <StatusBadge status={value} type="delivery" />;
      case "engagement_status":
        return <StatusBadge status={value} type="engagement" />;
      case "source":
        return <SourceBadge source={value} />;
      case "subject":
      case "notification_text":
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: "300px" }}>
            {value}
          </span>
        );
      default:
        return value;
    }
  };

  return (
    <tr>
      {columns.map((col) => (
        <td key={col.key}>{formatValue(col.key, notification[col.key])}</td>
      ))}
    </tr>
  );
}

function NotificationsTable({ notifications, channel }) {
  const getColumns = () => {
    switch (channel) {
      case "email":
        return [
          { key: "created_at", label: "Date" },
          { key: "notification_name", label: "Type" },
          { key: "subject", label: "Subject" },
          { key: "delivery_status", label: "Delivery" },
          { key: "engagement_status", label: "Engagement" },
          { key: "source", label: "Source" },
        ];
      case "web_push":
        return [
          { key: "created_at", label: "Date" },
          { key: "notification_name", label: "Type" },
          { key: "subject", label: "Subject" },
          { key: "delivery_status", label: "Delivery" },
          { key: "engagement_status", label: "Engagement" },
        ];
      case "webapp_notification":
        return [
          { key: "created_at", label: "Date" },
          { key: "notification_name", label: "Type" },
          { key: "notification_text", label: "Text" },
          { key: "seen_at", label: "Seen" },
          { key: "clicked_at", label: "Clicked" },
        ];
      default:
        return [];
    }
  };

  const columns = getColumns();

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover">
        <thead className="table-light">
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {notifications.map((notif) => (
            <NotificationRow key={notif.id} notification={notif} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelSelector({ value, onChange }) {
  const channels = [
    { value: "email", label: "Email", icon: "fa-envelope" },
    { value: "web_push", label: "Web Push", icon: "fa-bell" },
    { value: "webapp_notification", label: "In-App", icon: "fa-inbox" },
  ];

  return (
    <div className="btn-group" role="group" aria-label="Channel selector">
      {channels.map((ch) => (
        <button
          key={ch.value}
          type="button"
          className={`btn btn-sm ${value === ch.value ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => onChange(ch.value)}
        >
          <i className={`fa ${ch.icon} me-1`} />
          {ch.label}
        </button>
      ))}
    </div>
  );
}

function SentNotificationsTable({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, page: 1, per_page: 50, total_pages: 1 });
  const [channel, setChannel] = useState("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/admin/api/sent_notifications?user_id=${userId}&channel=${channel}&page=${page}`,
          {
            headers: new Headers(authenticatedHeadersHash()),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const data = await response.json();
        setNotifications(data.notifications || []);
        setMeta(data.meta || { total_count: 0, page: 1, per_page: 50, total_pages: 1 });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [userId, channel],
  );

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const handlePageChange = (newPage) => {
    fetchNotifications(newPage);
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fa fa-paper-plane me-2" />
          Sent Notifications
        </h5>
        <ChannelSelector value={channel} onChange={setChannel} />
      </div>
      <div className="card-body">
        {loading && (
          <div className="text-center p-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading notifications...</span>
          </div>
        )}

        {error && <div className="alert alert-danger mb-0">{error}</div>}

        {!loading && !error && notifications.length === 0 && (
          <div className="text-center text-muted p-4">
            No {channel.replace("_", " ")} notifications found for this user.
          </div>
        )}

        {!loading && !error && notifications.length > 0 && (
          <>
            <NotificationsTable notifications={notifications} channel={channel} />
            <Pagination meta={meta} onPageChange={handlePageChange} />
          </>
        )}
      </div>
    </div>
  );
}

function NotificationFullView({ diagnostics, userId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const userName = diagnostics ? [diagnostics.first_name, diagnostics.last_name].filter(Boolean).join(" ") : "";

  return (
    <div>
      <DiagnosticSummary diagnostics={diagnostics} onViewFullReport={() => setIsModalOpen(true)} />

      <DiagnosticModal diagnostics={diagnostics} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <SendgridSuppressionCard email={diagnostics?.email} userName={userName} />

      <SentNotificationsTable userId={userId} />
    </div>
  );
}

/**
 * AdminUserNotifications - Notifications component for admin user detail page
 *
 * Supports two modes:
 * - mode="card": Compact summary card with link to full view (for overview page)
 * - mode="full": Complete notification details with diagnostics, suppressions, and sent list
 *
 * @param {string} userId - User ID to fetch data for
 * @param {string} userSlug - User slug for generating links (required for card mode)
 * @param {string} mode - "card" or "full" (default: "full")
 */
function AdminUserNotifications({ userId, userSlug, mode = "full" }) {
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCardMode = mode === "card";

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        const { supabaseClient } = globalThis.Qwoted;
        const { data, error: fetchError } = await supabaseClient
          .from("user_notification_diagnostics")
          .select()
          .eq("user_id", userId)
          .maybeSingle();

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        setDiagnosticData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDiagnostics();
  }, [userId]);

  if (loading) return <LoadingState compact={isCardMode} label="Loading notification data..." />;
  if (error) return <ErrorState message={error} title="Unable to Load Diagnostics" compact={isCardMode} />;

  if (isCardMode) {
    return <NotificationCardView diagnostics={diagnosticData} userSlug={userSlug} />;
  }

  return <NotificationFullView diagnostics={diagnosticData} userId={userId} />;
}

export default AdminUserNotifications;
